import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Styles from "../BusinessDescriptionForm.module.css";

type SipocCategory = "Clients" | "outputs" | "inputs" | "suppliers" | "processes" | "resources" | "objectives";

interface SIPOCDiagramProps {
  sipocData: Record<SipocCategory, string[]>;
  setSipocData: React.Dispatch<React.SetStateAction<Record<SipocCategory, string[]>>>;
  isEditMode: boolean;
}

const SIPOCDiagramStep: React.FC<SIPOCDiagramProps> = ({ sipocData, setSipocData, isEditMode }) => {
  // Placeholder mappings for sipocData fields
  const sipocDataPlaceholders: Record<SipocCategory, string> = {
    Clients: "Who are our clients?",
    outputs: "Which are the products and services we deliver to them?",
    inputs: "Which are the inputs we need to deliver value?",
    suppliers: "Who are our suppliers?",
    processes: "Which are the processes we execute to deliver value?",
    resources: "Which are the resources we need to deliver value?",
    objectives: "How do we measure our processes' quality?",
  };

  // Handle input field changes
  const handleInputChange = (category: SipocCategory, index: number, value: string) => {
    setSipocData((prev) => ({
      ...prev,
      [category]: prev[category].map((item, i) => (i === index ? value : item)),
    }));
  };

  // Add a new field in the selected category
  const handleAddField = (category: SipocCategory) => {
    setSipocData((prev) => ({
      ...prev,
      [category]: [...prev[category], ""], // Adding a new field
    }));
  };

  // Remove a field from the selected category
  const handleRemoveField = (category: SipocCategory, index: number) => {
    setSipocData((prev) => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index), // Removing the selected field
    }));
  };

  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ color: "#000", pt: '20px' }}>
        SIPOC Diagram {isEditMode && "(Editing Draft)"}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "50% 50%",
          gap: 2,
        }}
      >
        {Object.entries(sipocData).map(([category, fields]) => (
          <Box key={category}>
            <Typography variant="subtitle1" gutterBottom>
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </Typography>
            {(fields as string[]).map((field, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                  color: "#000",
                }}
              >
                <input
                  className={Styles.inputFeild}
                  value={field}
                  onChange={(e) =>
                    handleInputChange(category as SipocCategory, index, e.target.value)
                  }
                  placeholder={sipocDataPlaceholders[category as SipocCategory]}
                />
                {index === fields.length - 1 ? (
                  <IconButton
                    sx={{ color: "#000" }}
                    onClick={() => handleAddField(category as SipocCategory)}
                  >
                    <AddIcon />
                  </IconButton>
                ) : (
                  <IconButton
                    sx={{ color: "#000" }}
                    onClick={() => handleRemoveField(category as SipocCategory, index)}
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </>
  );
};

export default SIPOCDiagramStep;